import React, { useState, useEffect, useContext } from "react";
import qs from "query-string";

import * as Yup from "yup";
import { useHistory } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import { Formik, Form, Field } from "formik";
import usePlans from "../../hooks/usePlans";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import InputMask from "react-input-mask";
import { MenuItem, IconButton, Menu } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import logoLight from "../../assets/logo.png";
import { i18n } from "../../translate/i18n";
import {
  TranslateOutlined,
  WbSunnyOutlined,
  NightsStayOutlined,
  ExpandMoreOutlined,
} from "@material-ui/icons";
import LanguageControl from "../../components/LanguageControl";
import ColorModeContext from "../../layout/themeContext";

import { openApi } from "../../services/api";
import toastError from "../../errors/toastError";
import moment from "moment";
import { formatCpfCnpj, isValidCpfCnpj } from "../../utils/cpfCnpj";

/* ======= Validação ======= */
const UserSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, i18n.t("signup.formErrors.name.short"))
    .max(50, i18n.t("signup.formErrors.name.long"))
    .required(i18n.t("signup.formErrors.name.required")),
  password: Yup.string()
    .required(i18n.t("signup.formErrors.password.required"))
    .min(8, i18n.t("signup.formErrors.password.short"))
    .max(50, i18n.t("signup.formErrors.password.long"))
    .matches(
      /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/,
      i18n.t("signup.formErrors.password.weak")
    ),
  email: Yup.string()
    .email(i18n.t("signup.formErrors.email.invalid"))
    .required(i18n.t("signup.formErrors.email.required")),
  cpfCnpj: Yup.string()
    .required(i18n.t("signup.formErrors.cpfCnpj.required"))
    .test("cpf-cnpj", i18n.t("signup.formErrors.cpfCnpj.invalid"), (value) => isValidCpfCnpj(value)),
});

/* Largura máxima do dropdown */
const DROPDOWN_WIDTH = 300;

/**
 * ==== Estilos Floating UI ====
 * Cores sólidas, sombras profundas, elementos flutuantes
 * Sem glassmorphism, sem scroll
 */
const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";

  return {
    // Container principal - ocupa viewport inteira, sem scroll
    pageRoot: {
      width: "100vw",
      height: "100vh",
      overflow: "hidden",
      backgroundColor: isLight ? theme.palette.primary.main : "#1c1c1c",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
    },

    // Card flutuante com sombras profundas
    paper: {
      backgroundColor: "#ffffff",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "40px 32px",
      borderRadius: theme.neoUI?.floating.card?.borderRadius || 20,
      boxShadow: theme.neoUI?.floating.card?.boxShadow || "0 20px 60px -10px rgba(0, 0, 0, 0.15)",
      border: theme.neoUI?.floating.card?.border || "1px solid rgba(0, 0, 0, 0.04)",
      transition: "transform 0.3s ease, box-shadow 0.3s ease",
      maxWidth: 440,
      width: "100%",
      maxHeight: "90vh",
      overflowY: "auto",
      // Keeps every field and the login link visible on short laptop screens.
      "@media (max-height: 860px)": {
        padding: "24px 32px",
        maxHeight: "96vh",
      },
      "&:hover": {
        boxShadow: theme.neoUI?.floating.card?.hoverShadow || "0 28px 70px -10px rgba(0, 0, 0, 0.18)",
      },
      // Scrollbar styling for overflow scenarios
      "&::-webkit-scrollbar": {
        width: 6,
      },
      "&::-webkit-scrollbar-thumb": {
        background: "rgba(0, 0, 0, 0.15)",
        borderRadius: 3,
      },
    },

    // Formulário
    form: {
      width: "100%",
      marginTop: theme.spacing(2),
    },

    // Botão com sombra verde
    submit: {
      ...theme.neoUI.buttons.action.root,
      margin: theme.spacing(2, 0, 1.5),
      borderRadius: 12,
      padding: "14px",
      fontSize: "1rem",
      height: "auto",
      minHeight: "auto",
      transition: "all 0.3s ease-in-out",
      "&:hover": {
        ...theme.neoUI.buttons.action.root["&:hover"],
      },
    },

    // Controles de idioma e tema (posicionados no topo)
    languageControl: {
      position: "absolute",
      top: 16,
      left: 16,
    },
    themeControl: {
      position: "absolute",
      top: 16,
      right: 16,
    },

    // Campos de entrada com sombra interna
    registerFieldRoot: {
      backgroundColor: "#ffffff",
      borderRadius: 10,
      boxShadow: theme.neoUI?.floating.input?.boxShadow || "inset 0 2px 4px rgba(0, 0, 0, 0.04)",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(0, 0, 0, 0.12)",
        borderWidth: 1,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
      "&.Mui-focused": {
        boxShadow: theme.neoUI?.floating.input?.focusShadow || "0 0 0 3px rgba(20, 164, 92, 0.15)",
      },
      "& input:-webkit-autofill": {
        WebkitTextFillColor: "#000 !important",
        WebkitBoxShadow: "0 0 0 1000px #fff inset !important",
      },
    },
    registerInput: {
      color: "#000",
      padding: "14px 16px",
      "&::placeholder": { color: "#9EA3AA", opacity: 1 },
    },

    // Altura uniforme para todos os campos
    uniformField: {
      "& .MuiOutlinedInput-input": {
        padding: "14px 16px",
      },
      "& .MuiSelect-select": {
        padding: "14px 16px",
      },
    },

    // SELECT (Plano) – estilo consistente
    selectFieldRoot: {
      backgroundColor: "#ffffff",
      borderRadius: 10,
      boxShadow: theme.neoUI?.floating.input?.boxShadow || "inset 0 2px 4px rgba(0, 0, 0, 0.04)",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(0, 0, 0, 0.12)",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
      "& .MuiSelect-select": {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        whiteSpace: "normal",
        wordBreak: "break-word",
        lineHeight: 1.3,
        textAlign: "left",
        width: "100%",
        paddingRight: theme.spacing(4),
      },
      "& .MuiSelect-icon": {
        color: theme.palette.primary.main,
      },
    },
    selectPlaceholder: {
      color: "#9EA3AA",
      display: "block",
      width: "100%",
      textAlign: "left",
    },

    // Logo
    logo: {
      width: "60%",
      maxWidth: 180,
      marginBottom: theme.spacing(1),
      "@media (max-height: 860px)": {
        maxHeight: 76,
        width: "auto",
      },
    },
  };
});

const SignUp = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isLight = theme.palette.type === "light";
  const colorMode = useContext(ColorModeContext);
  const history = useHistory();

  const [anchorElLanguage, setAnchorElLanguage] = useState(null);
  const [menuLanguageOpen, setMenuLanguageOpen] = useState(false);

  const handleMenuLanguage = (event) => {
    setAnchorElLanguage(event.currentTarget);
    setMenuLanguageOpen(true);
  };

  const handleCloseMenuLanguage = () => {
    setAnchorElLanguage(null);
    setMenuLanguageOpen(false);
  };

  const handleToggleTheme = () => {
    if (colorMode?.toggleColorMode) {
      colorMode.toggleColorMode();
    }
  };

  let companyId = null;
  const params = qs.parse(window.location.search);
  if (params.companyId !== undefined) {
    companyId = params.companyId;
  }

  const initialState = {
    name: "",
    cpfCnpj: "",
    email: "",
    phone: "",
    password: "",
    planId: "",
  };

  const [user] = useState(initialState);
  const dueDate = moment().add(3, "day").format();

  const handleSignUp = async (values) => {
    Object.assign(values, { recurrence: "MENSAL" });
    Object.assign(values, { dueDate });
    Object.assign(values, { status: "t" });
    Object.assign(values, { campaignsEnabled: true });

    try {
      await openApi.post("/companies/cadastro", values);
      toast.success(i18n.t("signup.toasts.success"));
      history.push("/login");
    } catch (err) {
      console.log(err);
      toastError(err);
    }
  };

  const [plans, setPlans] = useState([]);
  const { getPlanList } = usePlans();

  const allowedPlanNames = ["starter", "plus", "pro", "business"];
  const planOrder = { starter: 0, plus: 1, pro: 2, business: 3 };

  useEffect(() => {
    async function fetchData() {
      const list = await getPlanList();
      const filtered = Array.isArray(list)
        ? list
            .filter((plan) => {
              const normalized = plan?.name?.toLowerCase();
              return normalized && allowedPlanNames.includes(normalized);
            })
            .sort((a, b) => {
              const orderA = planOrder[a.name.toLowerCase()] ?? 99;
              const orderB = planOrder[b.name.toLowerCase()] ?? 99;
              return orderA - orderB;
            })
        : [];
      setPlans(filtered);
    }
    fetchData();
  }, [getPlanList]);

  // Mostrar apenas nome + valor (selecionado)
  const renderPlanValue = (selected) => {
    if (!selected) return <span className={classes.selectPlaceholder}>Plano</span>;
    const opt = plans.find((o) => o.id === selected);
    if (!opt) return selected;
    const text = `${opt.name} – R$ ${opt.value}`;
    return (
      <span style={{ whiteSpace: "normal", color: "#000", display: "block", textAlign: "left" }}>
        {text}
      </span>
    );
  };

  return (
    <div className={classes.pageRoot}>
      {/* Seletor de idioma */}
      <div className={classes.languageControl}>
        <IconButton edge="start">
          <TranslateOutlined
            aria-label="language selector"
            aria-controls="menu-signup-language"
            aria-haspopup="true"
            onClick={handleMenuLanguage}
            style={{ color: "#FFFFFF" }}
          />
        </IconButton>
        <Menu
          id="menu-signup-language"
          anchorEl={anchorElLanguage}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          open={menuLanguageOpen}
          onClose={handleCloseMenuLanguage}
        >
          <MenuItem>
            <LanguageControl />
          </MenuItem>
        </Menu>
      </div>

      {/* Botão de tema */}
      <div className={classes.themeControl}>
        <IconButton edge="end" onClick={handleToggleTheme} aria-label="toggle theme">
          {isLight ? (
            <NightsStayOutlined style={{ color: "#FFFFFF" }} />
          ) : (
            <WbSunnyOutlined style={{ color: "#FFFFFF" }} />
          )}
        </IconButton>
      </div>

      <CssBaseline />
      <Container component="main" maxWidth="xs">
        <div className={classes.paper}>
          <img
            className={classes.logo}
            src={logoLight}
            alt="NeoZapp"
          />

          <Formik
            initialValues={user}
            enableReinitialize={true}
            validationSchema={UserSchema}
            onSubmit={(values, actions) => {
              setTimeout(() => {
                handleSignUp(values);
                actions.setSubmitting(false);
              }, 400);
            }}
          >
            {({ touched, errors, setFieldValue }) => (
              <Form className={classes.form}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      autoComplete="name"
                      name="name"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant="outlined"
                      fullWidth
                      className={classes.uniformField}
                      InputProps={{
                        notched: false,
                        classes: {
                          root: classes.registerFieldRoot,
                          input: classes.registerInput,
                        },
                      }}
                      id="name"
                      placeholder={i18n.t("signup.form.name")}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      name="cpfCnpj"
                      onChange={(event) => setFieldValue("cpfCnpj", formatCpfCnpj(event.target.value))}
                      error={touched.cpfCnpj && Boolean(errors.cpfCnpj)}
                      helperText={touched.cpfCnpj && errors.cpfCnpj}
                      variant="outlined"
                      fullWidth
                      required
                      className={classes.uniformField}
                      InputProps={{
                        notched: false,
                        classes: {
                          root: classes.registerFieldRoot,
                          input: classes.registerInput,
                        },
                      }}
                      inputProps={{ maxLength: 18, "aria-label": i18n.t("signup.form.cpfCnpj") }}
                      id="cpfCnpj"
                      placeholder={`${i18n.t("signup.form.cpfCnpj")} *`}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      variant="outlined"
                      fullWidth
                      className={classes.uniformField}
                      InputProps={{
                        notched: false,
                        classes: {
                          root: classes.registerFieldRoot,
                          input: classes.registerInput,
                        },
                      }}
                      id="email"
                      placeholder={`${i18n.t("signup.form.email")} *`}
                      name="email"
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      autoComplete="email"
                      required
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Field as={InputMask} mask="(99) 99999-9999" id="phone" name="phone" required>
                      {({ field }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          fullWidth
                          className={classes.uniformField}
                          InputProps={{
                            notched: false,
                            classes: {
                              root: classes.registerFieldRoot,
                              input: classes.registerInput,
                            },
                          }}
                          placeholder={i18n.t("signup.form.phone")}
                        />
                      )}
                    </Field>
                  </Grid>

                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      variant="outlined"
                      fullWidth
                      className={classes.uniformField}
                      InputProps={{
                        notched: false,
                        classes: {
                          root: classes.registerFieldRoot,
                          input: classes.registerInput,
                        },
                      }}
                      name="password"
                      error={touched.password && Boolean(errors.password)}
                      helperText={(touched.password && errors.password) || i18n.t("signup.form.passwordHint")}
                      placeholder={`${i18n.t("signup.form.password")} *`}
                      type="password"
                      id="password"
                      autoComplete="current-password"
                      required
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      select
                      variant="outlined"
                      fullWidth
                      className={classes.uniformField}
                      name="planId"
                      SelectProps={{
                        displayEmpty: true,
                        IconComponent: ExpandMoreOutlined,
                        renderValue: renderPlanValue,
                        MenuProps: {
                          getContentAnchorEl: null,
                          anchorOrigin: { vertical: "bottom", horizontal: "left" },
                          transformOrigin: { vertical: "top", horizontal: "left" },
                          PaperProps: {
                            style: { width: DROPDOWN_WIDTH, maxWidth: DROPDOWN_WIDTH },
                          },
                          MenuListProps: {
                            style: { whiteSpace: "normal", wordBreak: "break-word" },
                          },
                        },
                      }}
                      InputProps={{
                        notched: false,
                        classes: {
                          root: `${classes.selectFieldRoot} ${classes.registerFieldRoot}`,
                        },
                      }}
                    >
                      <MenuItem value="">
                        <span className={classes.selectPlaceholder}>Plano</span>
                      </MenuItem>

                      {plans.map((plan) => (
                        <MenuItem
                          key={plan.id}
                          value={plan.id}
                          style={{ whiteSpace: "normal", wordBreak: "break-word" }}
                        >
                          {plan.name} – R$ {plan.value}
                        </MenuItem>
                      ))}
                    </Field>
                  </Grid>
                </Grid>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  className={classes.submit}
                >
                  {i18n.t("signup.buttons.submit")}
                </Button>

                <Grid container justifyContent="center">
                  <Grid item>
                    <Link href="#" variant="body2" component={RouterLink} to="/login">
                      {i18n.t("signup.buttons.login")}
                    </Link>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </div>
      </Container>
    </div>
  );
};

export default SignUp;
